/** Показ текущих метрик набора: WPM, CPM, точность, ритм, исправления. */
import type { TypingMetrics } from '@type2x/shared-contracts';

export interface MetricsBarProps {
  readonly metrics: TypingMetrics;
  readonly labels: {
    readonly wpm: string;
    readonly cpm: string;
    readonly accuracy: string;
    readonly rhythm: string;
    readonly corrections: string;
  };
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * Порядок и вес метрик (ТЗ UI-29, UI-46).
 *
 * Первыми стоят три, по которым человек судит о занятии: темп, точность
 * набора и ровность ритма. CPM и исправления вторичны — они уточняют первые
 * три, а не спорят с ними за внимание с текстом упражнения.
 */
export function MetricsBar({ metrics, labels }: MetricsBarProps): React.JSX.Element {
  const entries = [
    { key: 'wpm', label: labels.wpm, value: Math.round(metrics.wpm), primary: true },
    // Показывается точность набора, а не результата: «100% и двадцать
    // исправлений» — это про итоговый текст, а человек спрашивает про себя
    // (ТЗ FR-EN-77).
    {
      key: 'accuracy',
      label: labels.accuracy,
      value: percent(metrics.firstTryAccuracy),
      primary: true,
    },
    { key: 'rhythm', label: labels.rhythm, value: percent(metrics.rhythmStability), primary: true },
    { key: 'cpm', label: labels.cpm, value: Math.round(metrics.cpm), primary: false },
    {
      key: 'corrections',
      label: labels.corrections,
      value: metrics.corrections,
      primary: false,
    },
  ];

  /*
   * Метрики не объявляются экранным диктором (ТЗ UI-29).
   *
   * Живая область здесь читалась заново на каждом нажатии: человек, который
   * слушает экран, слышал вместо занятия поток чисел и не слышал самого
   * текста. Числа остаются доступными — список читается по требованию, — но
   * сами о себе не заявляют.
   */
  return (
    <dl className="t2x-metrics">
      {entries.map((entry) => (
        <div
          key={entry.key}
          className={`t2x-metrics__item t2x-metrics__item--${entry.primary ? 'primary' : 'secondary'}`}
        >
          <dt className="t2x-metrics__label">{entry.label}</dt>
          <dd className="t2x-metrics__value">{entry.value}</dd>
        </div>
      ))}
    </dl>
  );
}
